import { baseApi } from '@app/baseApi';

import type {
  Application,
  ApplicationIntegrationCount,
  CreateApplicationRequest,
  InviteUsersRequest,
  RecommendedAction,
  ResourceType,
  UpdateApplicationRequest,
  UserPermission,
} from '@/types';

import {
  transformCreateApplicationRequest,
  transformCreateApplicationResponse,
  transformGetApplicationIntegrationCountsResponse,
  transformGetApplicationResponse,
  transformGetApplicationsResponse,
  transformGetRecommendedActionsResponse,
  transformGetResourceTypesResponse,
  transformGetWorkspaceUsersResponse,
  transformInviteWorkspaceUsersRequest,
  transformUpdateApplicationRequest,
} from './apps.mappers';

export const appsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all applications
    getApplications: builder.query<Application[], void>({
      query: () => ({ url: 'applications', endpoint: 'main' }),
      transformResponse: transformGetApplicationsResponse,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'App' as const, id: String(id) })),
              { type: 'App', id: 'LIST' },
            ]
          : [{ type: 'App', id: 'LIST' }],
    }),

    // Get application integration counts
    getApplicationIntegrationCounts: builder.query<ApplicationIntegrationCount[], void>({
      query: () => ({ url: 'applicationsintegrationscount', endpoint: 'main' }),
      transformResponse: transformGetApplicationIntegrationCountsResponse,
    }),

    // Get single application
    getApplication: builder.query<Application, string>({
      query: (appId) => ({ url: `appinfo/${appId}`, endpoint: 'main' }),
      transformResponse: transformGetApplicationResponse,
      providesTags: (_result, _error, appId) => [{ type: 'App', id: appId }],
    }),

    // Create application
    createApplication: builder.mutation<
      Application,
      { tenantId: string; data: CreateApplicationRequest }
    >({
      query: ({ tenantId, data }) => ({
        url: `applications/create/${tenantId}`,
        endpoint: 'main',
        method: 'POST',
        body: transformCreateApplicationRequest(data),
      }),
      transformResponse: transformCreateApplicationResponse,
      invalidatesTags: [{ type: 'App', id: 'LIST' }],
    }),

    // Update application
    // Note: API returns 201 with empty body, so we invalidate cache to trigger refetch
    updateApplication: builder.mutation<
      void,
      { tenantId: string; appId: string; data: UpdateApplicationRequest }
    >({
      query: ({ tenantId, appId, data }) => ({
        url: `${tenantId}/applications/update/${appId}`,
        endpoint: 'main',
        method: 'POST',
        body: transformUpdateApplicationRequest(data),
      }),
      invalidatesTags: (_result, _error, { appId }) => [
        { type: 'App', id: appId },
        { type: 'App', id: 'LIST' },
      ],
    }),

    // Get resource types for an app
    getResourceTypes: builder.query<ResourceType[], string>({
      query: (appId) => ({ url: `resourcetypes/${appId}`, endpoint: 'main' }),
      transformResponse: transformGetResourceTypesResponse,
    }),

    // Get recommended actions by access level
    getRecommendedActions: builder.query<RecommendedAction[], string>({
      query: (accessLevel) => ({
        url: `recommendedactions/${accessLevel}`,
        endpoint: 'main',
      }),
      transformResponse: transformGetRecommendedActionsResponse,
    }),

    // Get workspace users/permissions
    getWorkspaceUsers: builder.query<UserPermission[], { tenantId: string; appId: string }>({
      query: ({ tenantId, appId }) => ({
        url: `${tenantId}/${appId}/permissions`,
        endpoint: 'main',
      }),
      transformResponse: transformGetWorkspaceUsersResponse,
      providesTags: (result, _error, { appId }) =>
        result
          ? [
              ...result.map(({ userId }) => ({ type: 'User' as const, id: userId })),
              { type: 'User', id: `workspace-${appId}` },
            ]
          : [{ type: 'User', id: `workspace-${appId}` }],
    }),

    // Invite users to workspace
    inviteWorkspaceUsers: builder.mutation<
      void,
      { tenantId: string; appId: string; data: InviteUsersRequest }
    >({
      query: ({ tenantId, appId, data }) => ({
        url: `${tenantId}/${appId}/permissions`,
        endpoint: 'main',
        method: 'POST',
        body: transformInviteWorkspaceUsersRequest(data),
      }),
      invalidatesTags: (_result, _error, { appId }) => [{ type: 'User', id: `workspace-${appId}` }],
    }),

    // Update workspace user admin status
    updateWorkspaceUserAdmin: builder.mutation<
      void,
      { tenantId: string; appId: string; userId: number; isAdmin: boolean }
    >({
      query: ({ tenantId, appId, userId, isAdmin }) => ({
        url: `${tenantId}/${appId}/permissions/${userId}`,
        endpoint: 'main',
        method: 'PUT',
        body: { isAdmin },
      }),
      invalidatesTags: (_result, _error, { appId }) => [{ type: 'User', id: `workspace-${appId}` }],
    }),

    // Remove user from workspace
    removeWorkspaceUser: builder.mutation<
      void,
      { tenantId: string; appId: string; userId: number }
    >({
      query: ({ tenantId, appId, userId }) => ({
        url: `${tenantId}/${appId}/permissions/${userId}`,
        endpoint: 'main',
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { appId }) => [{ type: 'User', id: `workspace-${appId}` }],
    }),

    // Get analytics dashboard URL
    getAnalyticsDashboard: builder.query<{ url: string }, string>({
      query: (tenantId) => ({ url: `analytics/${tenantId}`, endpoint: 'main' }),
    }),

    // Get tenant credentials (API Keys)
    getTenantCredentials: builder.query<{ tykApiKey: string; tykSecretKey: string }, string>({
      query: (appId) => ({ url: `tenantcreds/${appId}`, endpoint: 'main' }),
      transformResponse: (response: { TykApiKey: string; TykSecretKey: string }) => ({
        tykApiKey: response.TykApiKey || '',
        tykSecretKey: response.TykSecretKey || '',
      }),
    }),
  }),
});

export const {
  useGetApplicationsQuery,
  useGetApplicationIntegrationCountsQuery,
  useGetApplicationQuery,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
  useGetResourceTypesQuery,
  useGetRecommendedActionsQuery,
  useGetWorkspaceUsersQuery,
  useInviteWorkspaceUsersMutation,
  useUpdateWorkspaceUserAdminMutation,
  useRemoveWorkspaceUserMutation,
  useGetAnalyticsDashboardQuery,
  useGetTenantCredentialsQuery,
} = appsApi;
